import {
  Button,
  Checkbox,
  Chip,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import dayjs from 'dayjs'
import React from 'react'

import {
  cancelSeat,
  cancelSession,
  fetchSessionAttendance,
  fetchSessions,
  fetchTeachers,
  markSessionTeacherAttendance,
  restoreSeat,
  toggleAttendance
} from 'api/apartment'
import { Loading } from 'app/components/generic/LottieComponents'
import Slider from 'app/components/generic/Slider/slider'
import { confirm } from 'app/helpers'
// The main attendance page's timeline chrome — one row per hour, cards wrapping
// inside it. Reused rather than reimplemented.
import * as pageStyles from 'app/pages/Attendance/styles'

import { SessionCard } from './SessionCard'

import ApartmentPageHeader from '../shared/ApartmentPageHeader'
import { useSelectedApartment } from '../shared/apartmentStore'

const todayISO = () => dayjs().format('YYYY-MM-DD')

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  controls: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  empty: { padding: theme.spacing(3), color: theme.palette.text.secondary },
  panel: { padding: theme.spacing(3) },
  counts: {
    display: 'flex',
    gap: theme.spacing(2),
    margin: theme.spacing(2, 0)
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  name: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  muted: { color: theme.palette.text.secondary, fontSize: 12 },
  // The teacher sits above the children and is visually separated from them:
  // it is one row about a different kind of person, and running it into the
  // roster makes it read as the first child in the list.
  teacherRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    marginBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.palette.divider}`
  },
  teacherPicker: { minWidth: 180 }
}))

// Matches the centre page's arrival options (`ARRIVAL_CHOICES`) and the
// backend's ArrivalStatus enum. Values are the stored ones; labels are for
// humans.
const ARRIVALS = [
  { value: 'early', label: 'Early' },
  { value: 'on_time', label: 'On time' },
  { value: 'late', label: 'Late' }
]

// The teacher's own attendance for a session: did they turn up, who actually
// took it, and how they arrived. The apartment twin of the teacher row on
// /classes/attendance/mark.
//
// Like the roster below it there is no Save button — every control PATCHes on
// change and the server returns the new state, so two coordinators on the same
// session cannot disagree about what was marked.
const TeacherRow = ({ classes, session, apartmentId, value, onChange }) => {
  const [teachers, setTeachers] = React.useState([])
  const [busy, setBusy] = React.useState(false)

  React.useEffect(() => {
    let active = true
    fetchTeachers(apartmentId)
      .then((rows) => active && setTeachers(rows))
      .catch(() => active && setTeachers([]))
    return () => {
      active = false
    }
  }, [apartmentId])

  const save = async (payload) => {
    setBusy(true)
    try {
      onChange(await markSessionTeacherAttendance(session.uuid, payload))
    } catch (error) {
      // request.js already toasts the backend's message.
    } finally {
      setBusy(false)
    }
  }

  const scheduled = value?.scheduledTeacher
  const actual = value?.actualTeacher
  const marked = value?.attended !== null && value?.attended !== undefined

  // A session with nobody rostered has no attendance to take. Say so rather
  // than rendering controls that would record a mark against no one.
  if (!scheduled) {
    return (
      <div className={classes.teacherRow}>
        <Typography className={classes.muted}>
          No teacher is assigned to this session — assign one on the Batches tab
          to mark their attendance.
        </Typography>
      </div>
    )
  }

  return (
    <div className={classes.teacherRow}>
      <Checkbox
        color="primary"
        disabled={busy}
        checked={value?.attended === true}
        onChange={(e) => save({ attended: e.target.checked })}
      />
      <strong>{actual?.name || scheduled.name}</strong>
      <Chip size="small" label="teacher" />
      {value?.isSubstitute && <Chip size="small" label="substitute" />}
      {marked && (
        <Chip
          size="small"
          label={value.attended ? 'present' : 'absent (no-show)'}
        />
      )}

      {/* Who actually took the class. Defaults to the scheduled teacher once
          marked present, so "present" always names someone. */}
      <Select
        className={classes.teacherPicker}
        disabled={busy}
        displayEmpty
        value={actual?.uuid || ''}
        onChange={(e) => save({ actual_teacher: e.target.value || null })}
      >
        <MenuItem value="">Taught by…</MenuItem>
        {teachers.map((t) => (
          <MenuItem key={t.uuid} value={t.uuid}>
            {t.name}
            {t.uuid === scheduled.uuid ? ' (scheduled)' : ''}
          </MenuItem>
        ))}
      </Select>

      {/* Arrival only means something for a teacher who turned up. */}
      <Select
        className={classes.teacherPicker}
        disabled={busy || value?.attended !== true}
        displayEmpty
        value={value?.arrival || ''}
        onChange={(e) => save({ arrival: e.target.value || null })}
      >
        <MenuItem value="">Arrival…</MenuItem>
        {ARRIVALS.map((a) => (
          <MenuItem key={a.value} value={a.value}>
            {a.label}
          </MenuItem>
        ))}
      </Select>

      {marked && (
        <Button
          size="small"
          disabled={busy}
          onClick={() => save({ attended: null })}
        >
          Clear
        </Button>
      )}
    </div>
  )
}

// The roster, marked one child at a time. Like the main system there is no Save
// button: each checkbox PATCHes immediately and the server owns the toggle.
const RosterPanel = ({ classes, session, apartmentId, onChanged }) => {
  const [roster, setRoster] = React.useState(null)
  const [teacher, setTeacher] = React.useState(null)
  const [busy, setBusy] = React.useState(false)

  const load = React.useCallback(() => {
    fetchSessionAttendance(session.uuid)
      .then((d) => {
        setRoster(d.roster)
        // Carried on the same response, so the teacher row costs no extra fetch.
        setTeacher(d.teacherAttendance || null)
      })
      .catch(() => setRoster([]))
  }, [session.uuid])

  React.useEffect(load, [load])

  const isPast = dayjs(session.start_datetime).isBefore(dayjs(), 'day')

  const apply = async (fn) => {
    setBusy(true)
    try {
      const d = await fn()
      setRoster(d.roster)
      onChanged()
    } catch (error) {
      // request.js toasts the backend's message (seat cancelled, etc.)
    } finally {
      setBusy(false)
    }
  }

  const onToggle = (row) => {
    const run = () => apply(() => toggleAttendance(session.uuid, row.child))
    // Marking a past session asks first, exactly as the main page does.
    if (isPast) confirm(run, 'You are marking attendance in the past')
    else run()
  }

  const onCancel = (row) =>
    confirm(
      () => apply(() => cancelSeat(session.uuid, row.child)),
      `Cancel ${row.childName}'s seat? It will not count against their attendance.`
    )

  const onRestore = (row) => apply(() => restoreSeat(session.uuid, row.child))

  // A whole session, off for everyone. The teacher and every parent in the
  // class get a WhatsApp.
  const onCancelSession = () =>
    confirm(async () => {
      setBusy(true)
      try {
        await cancelSession(session.uuid)
        onChanged()
      } catch (error) {
        // request.js toasts (already cancelled, already started)
      } finally {
        setBusy(false)
      }
    }, 'Cancel this whole session? The teacher and parents will be notified.')

  if (roster === null) return <Loading />

  const present = roster.filter((r) => r.attended === true).length
  const absent = roster.filter((r) => r.attended === false).length
  const cancelled = roster.filter((r) => r.cancelled).length

  return (
    <div className={classes.panel}>
      <Typography variant="h6">{session.programName}</Typography>
      <div className={classes.muted}>
        {session.batchName} ·{' '}
        {dayjs(session.start_datetime).format('ddd D MMM, h:mmA')}
      </div>

      <div className={classes.counts}>
        <Chip size="small" label={`Present ${present}`} />
        <Chip size="small" label={`Absent ${absent}`} />
        <Chip size="small" label={`Cancelled ${cancelled}`} />
        {session.computedStatus !== 'Cancelled' && (
          <Button size="small" disabled={busy} onClick={onCancelSession}>
            Cancel session
          </Button>
        )}
      </div>

      <TeacherRow
        classes={classes}
        session={session}
        apartmentId={apartmentId}
        value={teacher}
        onChange={setTeacher}
      />

      {roster.length === 0 ? (
        <Typography className={classes.empty}>
          No enrolled children for this session.
        </Typography>
      ) : (
        roster.map((r) => (
          <div className={classes.row} key={r.child}>
            <span className={classes.name}>
              <Checkbox
                color="primary"
                // A cancelled seat cannot be marked — the backend refuses it.
                disabled={busy || r.cancelled}
                checked={r.attended === true}
                onChange={() => onToggle(r)}
              />
              {r.childName}
              {/* A trial sitter is on the register of a batch they are not
                  enrolled in. Marking them present is what flips their trial
                  to Attended. */}
              {r.trial && <Chip size="small" label="trial" />}
              {r.cancelled && <Chip size="small" label="cancelled" />}
            </span>
            {r.cancelled ? (
              <Button size="small" disabled={busy} onClick={() => onRestore(r)}>
                Restore
              </Button>
            ) : (
              <Button size="small" disabled={busy} onClick={() => onCancel(r)}>
                Cancel seat
              </Button>
            )}
          </div>
        ))
      )}
    </div>
  )
}

const ApartmentAttendance = () => {
  const classes = useStyles()
  const [apartment] = useSelectedApartment()
  const [date, setDate] = React.useState(todayISO())
  const [sessions, setSessions] = React.useState(null)
  const [openId, setOpenId] = React.useState(null)
  const [reloadKey, setReloadKey] = React.useState(0)

  React.useEffect(() => {
    let active = true
    setSessions(null)
    fetchSessions(apartment.id, date)
      .then((s) => active && setSessions(s))
      .catch(() => active && setSessions([]))
    return () => {
      active = false
    }
  }, [apartment.id, date, reloadKey])

  const refresh = () => setReloadKey((k) => k + 1)

  if (sessions === null) return <Loading />

  // Grouped by the hour they start, as on the main page.
  const byHour = sessions.reduce((acc, s) => {
    const key = `${dayjs(s.start_datetime).format('HH')}:00`
    acc[key] = acc[key] || []
    acc[key].push(s)
    return acc
  }, {})
  const hours = Object.keys(byHour).sort(
    (a, b) => parseInt(a, 10) - parseInt(b, 10)
  )

  return (
    <div className={classes.root}>
      <ApartmentPageHeader title="Attendance" />

      <div className={classes.controls}>
        <TextField
          type="date"
          label="Date"
          size="small"
          variant="outlined"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </div>

      {sessions.length === 0 ? (
        <Typography className={classes.empty}>
          No sessions on this date. Sessions are generated from a batch&apos;s
          days between its start and end date.
        </Typography>
      ) : (
        <pageStyles.TimelineContainer>
          {hours.map((hour) => (
            <pageStyles.TimelineRow key={hour}>
              <pageStyles.TimeLabel>
                {dayjs(`1/1/1 ${hour}`).format('h:mm A')}
              </pageStyles.TimeLabel>
              <pageStyles.SchedulesContainer>
                {byHour[hour].map((s) => (
                  <Slider
                    key={s.uuid}
                    width="42%"
                    // A distinct blockType so the global SLIDER_OPENER event
                    // (emitted only by the parent dashboard) never matches.
                    blockType={`apartment-attendance-${s.uuid}`}
                    placeholder={
                      <div onClick={() => setOpenId(s.uuid)}>
                        <SessionCard session={s} />
                      </div>
                    }
                    onClose={() => setOpenId(null)}
                    childComponent={
                      openId === s.uuid ? (
                        <RosterPanel
                          classes={classes}
                          session={s}
                          apartmentId={apartment.id}
                          onChanged={refresh}
                        />
                      ) : (
                        <div />
                      )
                    }
                  />
                ))}
              </pageStyles.SchedulesContainer>
            </pageStyles.TimelineRow>
          ))}
        </pageStyles.TimelineContainer>
      )}
    </div>
  )
}

export default ApartmentAttendance
